import React, { useState } from 'react';

import { forgotPassword } from '../api';
import { SIGN_IN_PATHS } from '../constants';
import { readFlow, updateFlow } from '../flow';
import { isEmail } from '../rules';
import { Actions, BigButton, BigLink, Field, Frame, LinkRow, Messages, TextButton, TextLink, Title } from '../ui';
import { useLive } from '../useOutcome';
import { BAD_EMAIL } from './SignInScreen';

/**
 * Forgot password. The answer never says whether the email has an account:
 * every email gets the same neutral confirmation.
 */
export const ForgotScreen = () => {
  const [email, setEmail] = useState(() => readFlow().email || '');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [ok, setOk] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useLive();

  const send = async (again: boolean) => {
    if (busy) {
      return;
    }
    const trimmed = (again && sentTo) || email.trim();
    if (!isEmail(trimmed)) {
      setError(BAD_EMAIL);
      return;
    }
    setBusy(true);
    setError('');
    updateFlow({ email: trimmed });
    // Always 200; any failure still shows the same words, so nothing is given away.
    await forgotPassword(trimmed);
    if (!live.current) {
      return;
    }
    setBusy(false);
    setSentTo(trimmed);
    setOk(again ? `Sent again to ${trimmed}.` : `If ${trimmed} has a StrengthX account, a reset link is on its way.`);
  };

  return (
    <Frame>
      <Title title="Reset your password" sub="Enter the email you sign in with. We’ll send a link to choose a new password." />
      {sentTo ? (
        <>
          <Messages ok={ok} info="Didn’t get it? Check spam, or wait a minute and send it again. The link works for 1 hour." />
          <Actions>
            <BigLink to={SIGN_IN_PATHS.SIGN_IN} secondary>Back to sign in</BigLink>
          </Actions>
          <LinkRow>
            <TextButton onClick={() => send(true)} disabled={busy}>Send it again</TextButton>
          </LinkRow>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(false);
          }}
          noValidate
        >
          <Field
            id="sx-email"
            label="Email"
            value={email}
            onChange={(value) => {
              setEmail(value);
              setError('');
            }}
            inputMode="email"
            autoComplete="username"
            placeholder="you@example.com"
          />
          <Messages error={error} />
          <Actions>
            <BigButton type="submit" aria-busy={busy}>Send reset link</BigButton>
          </Actions>
          <LinkRow>
            <TextLink to={SIGN_IN_PATHS.SIGN_IN}>Back to sign in</TextLink>
          </LinkRow>
        </form>
      )}
    </Frame>
  );
};
